import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  patientsData,
  appointmentsData,
  getPatientById,
  servicesStats,
  monthlyPerformance,
  examReportsData,
  getReportsByStatus
} from "@/data/mockData";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";
import {
  Users,
  Calendar,
  Activity,
  TrendingUp,
  Clock,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Play,
  FileText,
  Send,
  AlertTriangle,
  PenTool,
  CalendarPlus
} from "lucide-react";
import { cn } from "@/lib/utils";

export const DashboardHome = () => {
  const navigate = useNavigate();
  const today = "2026-01-27";
  
  // Get today's appointments
  const todayAppointments = appointmentsData
    .filter(a => a.date === today)
    .sort((a, b) => a.time.localeCompare(b.time));

  // Stats
  const totalPatients = patientsData.length;
  const todayCount = todayAppointments.length;
  const completedToday = todayAppointments.filter(a => a.status === "completed").length;
  const totalServices = servicesStats.reduce((acc, s) => acc + s.count, 0);

  // Report stats
  const pendingReports = examReportsData.filter(r => r.status === 'pending' || r.status === 'in_review');
  const signedReports = examReportsData.filter(r => r.status === 'signed');
  const sentReports = examReportsData.filter(r => r.status === 'sent');
  const urgentPending = pendingReports.filter(r => r.priority === 'urgent');

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-success/10 text-success border-success/20 gap-1"><CheckCircle2 className="h-3 w-3" />Completato</Badge>;
      case "in-progress":
        return <Badge className="bg-warning/10 text-warning border-warning/20 gap-1"><Play className="h-3 w-3" />In corso</Badge>;
      default:
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Programmato</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Panoramica attività della farmacia</p>
        </div>
        <Button
          variant="premium"
          onClick={() => navigate("/booking?from=operator&pharmacyId=ph1")}
          data-testid="dashboard-new-booking-btn"
          className="gap-2"
        >
          <CalendarPlus className="h-4 w-4" />
          Nuova prenotazione
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalPatients}</p>
                <p className="text-sm text-muted-foreground">Pazienti Registrati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-info/10 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-info" />
              </div>
              <div>
                <p className="text-2xl font-bold">{todayCount}</p>
                <p className="text-sm text-muted-foreground">Appuntamenti Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedToday}/{todayCount}</p>
                <p className="text-sm text-muted-foreground">Completati Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalServices}</p>
                <p className="text-sm text-muted-foreground">Prestazioni Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Appointments */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Performance Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Andamento Prestazioni
            </CardTitle>
            <CardDescription>Ultimi 6 mesi</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyPerformance}>
                  <defs>
                    <linearGradient id="colorPrestazioni" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }} 
                  />
                  <Area
                    type="monotone"
                    dataKey="prestazioni"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#colorPrestazioni)"
                    name="Prestazioni"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Report Status Tracking */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Stato Referti
              </CardTitle>
            </div>
            <CardDescription>Tracciamento referti medici</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pending Reports */}
            <div className={cn(
              "p-4 rounded-lg border transition-all",
              pendingReports.length > 0 ? "bg-warning/5 border-warning/30" : "bg-muted/30"
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "h-10 w-10 rounded-lg flex items-center justify-center",
                    pendingReports.length > 0 ? "bg-warning/10" : "bg-muted"
                  )}>
                    <Clock className={cn(
                      "h-5 w-5",
                      pendingReports.length > 0 ? "text-warning" : "text-muted-foreground"
                    )} />
                  </div>
                  <div>
                    <p className="font-semibold">{pendingReports.length}</p>
                    <p className="text-xs text-muted-foreground">In attesa referto</p>
                  </div>
                </div>
                {urgentPending.length > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {urgentPending.length} urgenti
                  </Badge>
                )}
              </div>
            </div>

            {/* Signed - Ready to send */}
            <div className={cn(
              "p-4 rounded-lg border transition-all",
              signedReports.length > 0 ? "bg-success/5 border-success/30" : "bg-muted/30"
            )}>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "h-10 w-10 rounded-lg flex items-center justify-center",
                  signedReports.length > 0 ? "bg-success/10" : "bg-muted"
                )}>
                  <PenTool className={cn(
                    "h-5 w-5",
                    signedReports.length > 0 ? "text-success" : "text-muted-foreground"
                  )} />
                </div>
                <div>
                  <p className="font-semibold">{signedReports.length}</p>
                  <p className="text-xs text-muted-foreground">Firmati - da inviare</p>
                </div>
              </div>
            </div>

            {/* Sent */}
            <div className="p-4 rounded-lg border bg-primary/5 border-primary/30">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Send className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{sentReports.length}</p>
                  <p className="text-xs text-muted-foreground">Inviati ai pazienti</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="pt-2 border-t space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-between" 
                size="sm"
                onClick={() => window.open("/doctor/pending", "_blank")}
                data-testid="view-pending-reports-btn"
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Portale Medico
                </span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Appointments */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-info" />
              Appuntamenti Oggi
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/calendar")}>
              Vedi tutto
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {todayAppointments.slice(0, 6).map((apt, index) => {
              const patient = getPatientById(apt.patientId);
              return (
                <div
                  key={apt.id}
                  className={cn(
                    "p-3 rounded-lg border transition-colors cursor-pointer hover:bg-muted/50",
                    apt.status === "completed" && "bg-success/5 border-success/20",
                    apt.status === "in-progress" && "bg-warning/5 border-warning/20"
                  )}
                  onClick={() => navigate(`/dashboard/patients/${apt.patientId}`)}
                  data-testid={`appointment-${apt.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{apt.time}</span>
                    {getStatusBadge(apt.status)}
                  </div>
                  <p className="font-medium">{patient?.name}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {apt.services.join(", ")}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Top Services */}
      <Card>
        <CardHeader>
          <CardTitle>Prestazioni più Richieste</CardTitle>
          <CardDescription>Statistiche degli ultimi 6 mesi</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={servicesStats.slice(0, 6)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} width={120} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} name="Eseguite" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardHome;
